import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Luggage,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Scale,
  Sparkles,
  Cloud,
  Plane,
  Clock,
} from 'lucide-react';
import { PackingWorkflow } from '../packing/PackingWorkflow';

const features = [
  {
    icon: MapPin,
    title: 'Smart Destination Analysis',
    description: 'Weather forecasts and trip length tuned to your itinerary.',
  },
  {
    icon: Scale,
    title: 'Weight Optimization',
    description: 'Airline-specific weight estimates so you avoid baggage fees.',
  },
  {
    icon: CheckCircle2,
    title: 'Interactive Checklist',
    description: 'Track progress with smart tips along the way.',
  },
];

const stats = [
  { icon: Cloud, label: '10-day Weather' },
  { icon: Plane, label: 'Airline Rules' },
  { icon: Clock, label: '2-min Setup' },
];

export function PackingAssistant() {
  const [showPackingWorkflow, setShowPackingWorkflow] = useState(false);

  if (showPackingWorkflow) {
    return <PackingWorkflow onComplete={() => setShowPackingWorkflow(false)} />;
  }

  return (
    <div className="relative w-full max-w-5xl mx-auto px-2 sm:px-0">
      {/* Decorative gradient blobs */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-cyan-400/20 blur-3xl animate-blob-float" />
        <div
          className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full bg-teal-400/20 blur-3xl animate-blob-float"
          style={{ animationDelay: '2s' }}
        />
      </div>

      {/* Hero */}
      <div className="text-center mb-8 sm:mb-10 pt-4 sm:pt-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full border border-cyan-500/20 bg-cyan-500/5 text-xs sm:text-sm font-medium text-cyan-700 dark:text-cyan-300">
          <Sparkles className="h-3.5 w-3.5" />
          AI-powered Packing
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4">
          <span className="bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text text-transparent">
            Pack Right,
          </span>{' '}
          <span className="bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
            Travel Bright.
          </span>
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          A personalized packing list tailored to your destination, trip type and travel style — built in two minutes.
        </p>

        {/* Quick stat chips */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-6">
          {stats.map((s) => (
            <div
              key={s.label}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card text-xs sm:text-sm text-muted-foreground"
            >
              <s.icon className="h-3.5 w-3.5 text-cyan-500" />
              {s.label}
            </div>
          ))}
        </div>
      </div>

      {/* Feature grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-8 sm:mb-10">
        {features.map((f) => (
          <div
            key={f.title}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-cyan-500/30"
          >
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 opacity-10 blur-2xl transition-opacity group-hover:opacity-25" />
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/20">
              <f.icon className="h-6 w-6" />
            </div>
            <h3 className="font-semibold text-base sm:text-lg mb-1">{f.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
          </div>
        ))}
      </div>

      {/* Primary CTA card */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-card to-teal-500/10 p-6 sm:p-10 text-center">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.15),transparent_60%)]" />
        <div className="mx-auto mb-5 inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30">
          <Luggage className="h-7 w-7 sm:h-8 sm:w-8" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold mb-2">Ready to pack your next trip?</h3>
        <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto mb-6">
          Tell us where you're going — we'll handle the list.
        </p>
        <Button
          onClick={() => setShowPackingWorkflow(true)}
          size="lg"
          className="w-full sm:w-auto px-6 sm:px-10 min-h-[52px] sm:min-h-[56px] text-base sm:text-lg font-semibold touch-manipulation bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white shadow-lg shadow-cyan-500/30 border-0"
        >
          Start Packing My Bag
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <p className="text-xs sm:text-sm text-muted-foreground mt-4">
          Takes just 2 minutes — no card required.
        </p>
      </div>
    </div>
  );
}
